import type { KeyboardEvent } from "react";

/**
 * The keyboard half of the WAI-ARIA tabs pattern, shared by every strip that says
 * `role="tablist"` so they cannot drift apart.
 *
 * Put `onKeyDown={tabKeys}` on the tablist and `tabIndex={tabStop(i, selectedIndex)}` on each tab.
 * Left and Right move to the neighbouring tab and wrap round at the ends; Home and End jump to the
 * first and last. Focus and selection move together: the move *clicks* the tab it lands on, so
 * whatever activating that tab already does — navigating, switching a panel, turning the dial —
 * is exactly what the arrow key does, with no second code path to keep in step. Up and Down are
 * left alone because none of these strips is vertical.
 *
 * Focus is moved with the browser's own `focus()`, which scrolls the tab into view; a strip that
 * already follows its selection (the map's days) does so as well, and the two agree.
 */
export function tabKeys(e: KeyboardEvent<HTMLElement>): void {
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
  const list = e.currentTarget;
  const here = (e.target as Element).closest<HTMLElement>('[role="tab"]');
  // Only a tab of *this* list: a nested widget's keys, or an input inside the strip, are not ours.
  if (!here || here.closest('[role="tablist"]') !== list) return;
  const tabs = [...list.querySelectorAll<HTMLElement>('[role="tab"]')].filter(
    (t) => t.closest('[role="tablist"]') === list && !(t as HTMLButtonElement).disabled,
  );
  const at = tabs.indexOf(here);
  if (at < 0) return;
  let to: number;
  switch (e.key) {
    case "ArrowRight":
      to = (at + 1) % tabs.length;
      break;
    case "ArrowLeft":
      to = (at - 1 + tabs.length) % tabs.length;
      break;
    case "Home":
      to = 0;
      break;
    case "End":
      to = tabs.length - 1;
      break;
    default:
      return;
  }
  e.preventDefault();
  const next = tabs[to]!;
  next.focus();
  if (next !== here || next.getAttribute("aria-selected") !== "true") next.click();
}

/**
 * The roving tabindex: the selected tab is the strip's one stop in the page's tab order and the
 * rest are reached with the arrows. If nothing is selected the first tab takes the stop, so the
 * strip never becomes unreachable from the keyboard.
 */
export function tabStop(index: number, selected: number): 0 | -1 {
  return index === (selected < 0 ? 0 : selected) ? 0 : -1;
}
